import { BookingStatus, WaitAndReturnRole } from 'generated/graphql'
import { TBookingAddressForm } from 'types/form-types'

import { findReturnStopIndex } from './bookingDetails.helpers'

// Whether the stop is the return collection generated by Wait & Return.
export const isReturnCollection = (address?: TBookingAddressForm | null) =>
  address?.waitAndReturnRole === WaitAndReturnRole.ReturnCollection

// Whether the stop is the return delivery generated by Wait & Return.
export const isReturnDelivery = (address?: TBookingAddressForm | null) =>
  address?.waitAndReturnRole === WaitAndReturnRole.ReturnDelivery

// Whether the stop was generated by Wait & Return; user-created stops have no role.
export const isGeneratedReturnStop = (address?: TBookingAddressForm | null) =>
  isReturnCollection(address) || isReturnDelivery(address)

// The stops the user created, which are the ones every Wait & Return rule looks at.
export const getUserStops = (addresses: Array<TBookingAddressForm> | undefined) =>
  (addresses ?? []).filter((address) => !isGeneratedReturnStop(address))

// The sequence order for a new stop: one past the highest one on the booking.
export const getNextSequenceOrder = (
  collectionAddresses: Array<TBookingAddressForm> | undefined,
  deliveryAddresses: Array<TBookingAddressForm> | undefined,
) =>
  Math.max(
    0,
    ...[...(collectionAddresses ?? []), ...(deliveryAddresses ?? [])].map(
      (address) => address.sequenceOrder || 0,
    ),
  ) + 1

// The address and contact fields a generated stop takes over from its source stop.
const copyAddressDetails = (source: TBookingAddressForm) => ({
  name: source.name,
  city: source.city,
  addressLine1: source.addressLine1,
  addressLine2: source.addressLine2 || '',
  postcode: source.postcode,
  countryId: source.countryId,
  contact: source.contact || '',
  email: source.email || '',
  telephoneNumber: source.telephoneNumber || '',
  note: source.note || '',
  googleMapsPlaceId: source.googleMapsPlaceId || '',
  reasonCodeId: source.reasonCodeId ?? 0,
})

export type TReturnStopFieldUpdate = {
  sourcePath: string
  targetPath: string
  value: string | number
}

// The copied fields of a generated stop that no longer match its source stop, as form paths:
// the return collection follows the user delivery, the return delivery follows the user collection.
export const getReturnStopFieldUpdates = (
  collectionAddresses: Array<TBookingAddressForm> | undefined,
  deliveryAddresses: Array<TBookingAddressForm> | undefined,
): Array<TReturnStopFieldUpdate> => {
  const collections = collectionAddresses ?? []
  const deliveries = deliveryAddresses ?? []
  const pairs = [
    {
      sourceArray: 'deliveryAddresses',
      sourceIndex: deliveries.findIndex((address) => !isGeneratedReturnStop(address)),
      targetArray: 'collectionAddresses',
      targetIndex: collections.findIndex(isReturnCollection),
    },
    {
      sourceArray: 'collectionAddresses',
      sourceIndex: collections.findIndex((address) => !isGeneratedReturnStop(address)),
      targetArray: 'deliveryAddresses',
      targetIndex: deliveries.findIndex(isReturnDelivery),
    },
  ]

  return pairs.flatMap(({ sourceArray, sourceIndex, targetArray, targetIndex }) => {
    if (sourceIndex < 0 || targetIndex < 0) {
      return []
    }

    const source = sourceArray === 'deliveryAddresses' ? deliveries : collections
    const target = targetArray === 'deliveryAddresses' ? deliveries : collections
    const details = copyAddressDetails(source[sourceIndex])

    return Object.entries(details)
      .filter(([key, value]) => target[targetIndex][key as keyof typeof details] !== value)
      .map(([key, value]) => ({
        sourcePath: `${sourceArray}.${sourceIndex}.${key}`,
        targetPath: `${targetArray}.${targetIndex}.${key}`,
        value,
      }))
  })
}

// Builds the return collection at the delivery's address, with its contact and time window.
export const buildReturnCollection = (
  delivery: TBookingAddressForm,
  defaults: TBookingAddressForm,
  sequenceOrder: number,
): TBookingAddressForm => ({
  ...defaults,
  ...copyAddressDetails(delivery),
  at: delivery.at || '',
  by: delivery.by || '',
  sequenceOrder,
  waitAndReturnRole: WaitAndReturnRole.ReturnCollection,
})

// Builds the return delivery at the collection's address, with its contact and no time window.
export const buildReturnDelivery = (
  collection: TBookingAddressForm,
  defaults: TBookingAddressForm,
  sequenceOrder: number,
): TBookingAddressForm => ({
  ...defaults,
  ...copyAddressDetails(collection),
  sequenceOrder,
  waitAndReturnRole: WaitAndReturnRole.ReturnDelivery,
})

// Whether the booking is a there-and-back journey: one user collection and one user delivery.
export const isWaitAndReturnRouteValid = (
  collectionAddresses: Array<TBookingAddressForm> | undefined,
  deliveryAddresses: Array<TBookingAddressForm> | undefined,
) => getUserStops(collectionAddresses).length === 1 && getUserStops(deliveryAddresses).length === 1

// Whether the user added more than one collection or more than one delivery, which makes the
// booking a multidrop and rules Wait & Return out.
export const hasMultipleCollectionsOrDeliveries = (
  collectionAddresses: Array<TBookingAddressForm> | undefined,
  deliveryAddresses: Array<TBookingAddressForm> | undefined,
) => getUserStops(collectionAddresses).length > 1 || getUserStops(deliveryAddresses).length > 1

// Whether the return collection may still be generated: any status except a finished, invoiced
// or declined booking.
export const canGenerateReturnCollection = (status: BookingStatus | undefined) =>
  status !== BookingStatus.Complete &&
  status !== BookingStatus.Invoice &&
  status !== BookingStatus.Declined

// Marks the return delivery on a booking saved before the role existed, so the role-based rules
// apply to it. It is the delivery at the first collection's address, found anywhere in the route
// by the same search the Wait & Return pricing uses.
export const tagLegacyReturnDelivery = (
  collectionAddresses: Array<TBookingAddressForm>,
  deliveryAddresses: Array<TBookingAddressForm>,
  isWaitAndReturn: boolean | undefined,
): Array<TBookingAddressForm> => {
  const hasMarker = [...collectionAddresses, ...deliveryAddresses].some(isGeneratedReturnStop)
  const collection = collectionAddresses[0]

  if (!isWaitAndReturn || hasMarker || !collection || deliveryAddresses.length < 2) {
    return deliveryAddresses
  }

  const returnIndex = findReturnStopIndex(deliveryAddresses, collection)

  if (returnIndex === -1) {
    return deliveryAddresses
  }

  return deliveryAddresses.map((delivery, index) =>
    index === returnIndex
      ? { ...delivery, waitAndReturnRole: WaitAndReturnRole.ReturnDelivery }
      : delivery,
  )
}

export const resolveWaitAndReturnForCopy = (
  collectionAddresses: Array<TBookingAddressForm>,
  deliveryAddresses: Array<TBookingAddressForm>,
  isWaitAndReturn: boolean | undefined,
) => {
  const taggedDeliveryAddresses = tagLegacyReturnDelivery(
    collectionAddresses,
    deliveryAddresses,
    isWaitAndReturn,
  )

  if (
    isWaitAndReturn &&
    !hasMultipleCollectionsOrDeliveries(collectionAddresses, taggedDeliveryAddresses)
  ) {
    return {
      isWaitAndReturn: true,
      collectionAddresses,
      deliveryAddresses: taggedDeliveryAddresses,
    }
  }

  return {
    isWaitAndReturn: false,
    collectionAddresses: getUserStops(collectionAddresses),
    deliveryAddresses: getUserStops(taggedDeliveryAddresses),
  }
}
